// ContactPickerField.tsx — Anzeige + Auslöser der Kontaktauswahl in den
// Risiko-Formularen.
//
// Kein Fluent mehr: Auslöser ist ein schlichter Knopf im Refined-Stil
// (BRAND-Tokens), die Auswahl selbst läuft über `KontaktAuswahlPopup` →
// `redesign/RelationContactPicker` — dieselbe Auswahl wie in der Kundenakte
// („Verbindung zu Firma"), inklusive Person/Firma-Umschalter und
// Inline-Neuanlage. Der frühere Fluent-Dialog samt `ContactSelector` (nur
// Personen-/Firmensuche ohne Typwahl) ist damit weg.
//
// ACHTUNG — Datenschutz: Wer diese Komponente rendert, schaltet die Suche im
// Kontaktbestand frei. Sie gehört ausschließlich in den authentifizierten
// Weg; das Gate sitzt in `risikoImmobilieStep/KontaktFeld` (`lookup`).

import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "./tokens";
import KontaktAuswahlPopup from "./KontaktAuswahlPopup";
import type { ContactPickerValue, FieldDef } from "./types";

const useStyles = makeStyles({
  label: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "4px",
  },
  chip: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    ...shorthands.padding("12px", "14px"),
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  chipIcon: {
    color: BRAND.muted,
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  chipName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
    flexGrow: 1,
    minWidth: 0,
  },
  chipEmail: {
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  chipUnlinked: {
    marginLeft: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontStyle: "italic",
    color: BRAND.muted,
    whiteSpace: "nowrap",
  },
  emptyState: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
  },
  /**
   * Der Auslöser — bewusst ein nacktes `<button>`. Ohne globalen
   * box-sizing-Reset (der gilt in apps/web nur unter `.kvp-rd`) muss
   * `boxSizing` hier ausdrücklich stehen, sonst addiert sich das Padding auf
   * die Breite.
   */
  trigger: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "7px",
    flexShrink: 0,
    boxSizing: "border-box",
    cursor: "pointer",
    appearance: "none",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: BRAND.purple,
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    whiteSpace: "nowrap",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "13px",
    paddingRight: "13px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("6px"),
    transitionProperty: "background-color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "#f5eef6",
      ...shorthands.borderColor(BRAND.purple),
    },
    ":focus-visible": {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: BRAND.purple,
      outlineOffset: "2px",
    },
  },
  triggerIcon: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0,
  },
});

const PersonIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="10" cy="6.5" r="3.2" />
    <path d="M3.8 16.4c0-2.9 2.8-4.7 6.2-4.7s6.2 1.8 6.2 4.7" />
  </svg>
);

const WechselnIcon = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M13.2 3.6l3.2 3.2-8.6 8.6-3.9.7.7-3.9z" />
  </svg>
);

const AuswaehlenIcon = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="6.6" r="3" />
    <path d="M2.6 16c0-2.7 2.4-4.3 5.4-4.3 1 0 1.9.2 2.7.5" />
    <path d="M14.4 11.6v5.2M11.8 14.2H17" />
  </svg>
);

type Props = {
  field: FieldDef;
  value: ContactPickerValue | undefined;
  onChange: (key: string, value: ContactPickerValue) => void;
  hideLabel?: boolean;
  /**
   * Überschrift des Auswahl-Popups. War bis zur Beteiligten-Erfassung hart auf
   * „Eigentümer auswählen" verdrahtet — bei inzwischen einem Dutzend Rollen
   * (Mieter, Pächter, Betreiber, Gutachter …) irreführend. Default bleibt der
   * alte Text, damit Bestandsaufrufer unverändert bleiben.
   */
  dialogTitle?: string;
};

/**
 * E-Mail aus einem Kontakt-Rohobjekt. Die Suche liefert die Kanäle als
 * `communications`; gesucht wird gezielt der E-Mail-Kanal — der erste Kanal
 * überhaupt kann eine Telefonnummer sein und stand vorher fälschlich im
 * E-Mail-Feld.
 */
const emailAus = (c: any): string => {
  const direkt = String(c?.email ?? "").trim();
  if (direkt) return direkt;
  const kanaele = Array.isArray(c?.communications) ? c.communications : [];
  const treffer = kanaele.find(
    (k: any) =>
      String(k?.type ?? "").toLowerCase() === "email" && String(k?.value ?? "").trim(),
  );
  return String(treffer?.value ?? "").trim();
};

export const formatContact = (c: any): ContactPickerValue => {
  const id = typeof c?.id === "number" ? c.id : Number(c?.id) || null;
  const firstName = String(c?.firstName ?? c?.vorname ?? "").trim();
  const lastname = String(c?.lastname ?? c?.nachname ?? "").trim();
  const display =
    String(c?.displayName ?? "").trim() ||
    [firstName, lastname].filter(Boolean).join(" ") ||
    "—";
  const email = emailAus(c);
  return {
    contactId: id,
    label: display,
    email: email || undefined,
  };
};

const ContactPickerField: React.FC<Props> = ({
  field,
  value,
  onChange,
  hideLabel,
  dialogTitle = "Eigentümer auswählen",
}) => {
  const styles = useStyles();
  const [open, setOpen] = React.useState(false);

  const schliessen = React.useCallback(() => setOpen(false), []);

  const handleSelect = React.useCallback(
    (contact: unknown) => {
      if (!contact) return;
      onChange(field.key, formatContact(contact));
      setOpen(false);
    },
    [field.key, onChange],
  );

  const buttonLabel = value?.contactId ? "Wechseln" : "Auswählen";

  /**
   * Angezeigt wird, was da ist — nicht nur, was schon verknüpft ist. Ein Name
   * ohne `contactId` ist der Normalfall: Altbestand aus einem früheren
   * Freitextfeld und alles, was ein Kunde über das Token-Formular selbst
   * eingetragen hat. Hinge die Anzeige an der `contactId`, stünde beim
   * Bearbeiten „Noch kein Kontakt ausgewählt", obwohl der Wert gespeichert ist
   * und weiter mitgespeichert wird.
   */
  const hatInhalt = !!value?.contactId || !!value?.label?.trim();

  return (
    <div>
      {!hideLabel && field.label && (
        <span className={styles.label}>{field.label}</span>
      )}
      <div className={styles.chip}>
        <span className={styles.chipIcon}>{PersonIcon}</span>
        {hatInhalt ? (
          <>
            <span className={styles.chipName}>
              {value?.label || "—"}
              {!value?.contactId && (
                <span className={styles.chipUnlinked}>noch nicht verknüpft</span>
              )}
            </span>
            {value?.email && <span className={styles.chipEmail}>{value.email}</span>}
          </>
        ) : (
          <span className={mergeClasses(styles.chipName, styles.emptyState)}>
            Noch kein Kontakt ausgewählt.
          </span>
        )}
        <button type="button" className={styles.trigger} onClick={() => setOpen(true)}>
          <span className={styles.triggerIcon}>
            {value?.contactId ? WechselnIcon : AuswaehlenIcon}
          </span>
          {buttonLabel}
        </button>
      </div>
      <KontaktAuswahlPopup
        open={open}
        titel={dialogTitle}
        onClose={schliessen}
        onSelect={handleSelect}
      />
    </div>
  );
};

export default ContactPickerField;
